/**
 * Team Spain v0 — Tipos de Planificación Deportiva (M3)
 *
 * Contratos de datos para:
 * - Catálogo Ofensivo: Conceptos (Base, Dinámico, Hydra, Standard) y Jugadas.
 * - Catálogo Defensivo: Llamadas defensivas y familias de cobertura.
 * - Quintetos Reutilizables: Plantillas y slots con 5 posiciones canónicas.
 * - Guiones de Bloques Team: Práctica guiada / Team competitivo y repeticiones previstas.
 */

export type PrimaryResult =
  | 'pass_complete'
  | 'pass_incomplete'
  | 'interception'
  | 'sack'
  | 'run'
  | 'other'

export type SecondaryEventType =
  | 'pass_attempt'
  | 'pass_completion'
  | 'target'
  | 'catch'
  | 'drop'
  | 'interception_thrown'
  | 'interception'
  | 'flag_attempt_success'
  | 'flag_attempt_miss'
  | 'pass_defended'
  | 'sack'
  | 'pressure'
  | 'touchdown'
  | 'conversion_attempt'
  | 'conversion_success'
  | 'assignment_error'
  | 'positive_execution'

export type SnapMode = 'guided_practice' | 'competitive_team' | 'match'

export type OffensePosition = 'QB' | 'C' | 'Slot' | 'X' | 'Z'
export type DefensePosition = 'Blitzer' | 'LB1' | 'LB2' | 'CB1' | 'CB2'
export type SportPosition = OffensePosition | DefensePosition

export const OFFENSE_POSITIONS: OffensePosition[] = ['QB', 'C', 'Slot', 'X', 'Z']
export const DEFENSE_POSITIONS: DefensePosition[] = ['Blitzer', 'LB1', 'LB2', 'CB1', 'CB2']

export type ItemStatus = 'active' | 'archived'
export type ScriptMode = 'guided_practice' | 'competitive_team'
export type ScriptStatus = 'draft' | 'ready'

export interface OffensiveConcept {
  id: string
  organization_id: string
  team_id: string
  name: string
  description?: string | null
  status: ItemStatus
  created_at: string
  updated_at: string
}

export interface OffensivePlay {
  id: string
  organization_id: string
  team_id: string
  concept_id: string
  name: string
  short_code: string
  description?: string | null
  coaching_notes?: string | null
  status: ItemStatus
  concept?: OffensiveConcept
  created_at: string
  updated_at: string
}

export interface DefensiveCall {
  id: string
  organization_id: string
  team_id: string
  name: string
  short_code: string
  coverage_family?: string | null
  description?: string | null
  coaching_notes?: string | null
  status: ItemStatus
  created_at: string
  updated_at: string
}

export interface LineupSlot {
  athlete_id: string
  position: string
  athlete_name?: string
  full_name?: string
  dorsal_number?: number | null
  jersey_number?: number | null
  primary_position?: string
  was_substituted?: boolean
  substitution_reason?: string | null
}

export interface LineupTemplateSlot {
  id: string
  template_id: string
  team_id: string
  athlete_id: string
  position: string
  order_index: number
  athlete?: {
    id: string
    first_name: string
    last_name: string
    dorsal_number?: number | null
    primary_position: string
  }
}

export interface LineupTemplate {
  id: string
  organization_id: string
  team_id: string
  unit: 'offense' | 'defense'
  name: string
  status: ItemStatus
  slots: LineupTemplateSlot[]
  created_at: string
  updated_at: string
}

export interface PlannedRep {
  id: string
  script_id: string
  team_id: string
  order_index: number

  // Ofensiva prevista
  offense_lineup_template_id?: string | null
  offense_lineup_name: string
  offense_lineup_is_custom: boolean
  offense_slots: LineupSlot[]
  offense_concept_id?: string | null
  offense_concept_name?: string | null
  offense_play_id?: string | null
  offense_play_name?: string | null
  offense_play_code?: string | null
  offense_adjustments?: string | null

  // Defensiva prevista
  defense_lineup_template_id?: string | null
  defense_lineup_name: string
  defense_lineup_is_custom: boolean
  defense_slots: LineupSlot[]
  defense_call_id?: string | null
  defense_call_name?: string | null
  defense_call_code?: string | null
  defense_adjustments?: string | null

  // Contexto y notas
  initial_situation?: string | null
  staff_notes?: string | null
  created_at: string
  updated_at: string
}

export interface PracticeScript {
  id: string
  organization_id: string
  team_id: string
  session_id?: string | null
  block_id?: string | null
  name: string
  objective?: string | null
  mode: ScriptMode
  internal_notes?: string | null
  status: ScriptStatus
  reps?: PlannedRep[]
  reps_count?: number
  created_at: string
  updated_at: string
}

export interface ScriptConflict {
  type: 'dual_sided_player' | 'attendance_withdrawn' | 'attendance_declined'
  repIndex: number
  athleteId: string
  athleteName: string
  offensePosition?: string
  defensePosition?: string
  description: string
}

export interface RosterAthleteOption {
  id: string
  first_name: string
  last_name: string
  full_name: string
  dorsal_number?: number | null
  primary_position: string
  unit: 'offense' | 'defense'
  user_id?: string | null
  attendance_status?: 'confirmed' | 'pending' | 'declined' | 'withdrawn' | 'not_summoned'
}
